import type { AuthRules, SessionPayload } from '@wr/shared';
import { currentUserId, setCurrentUserId } from './device.js';
import { LocalStore, type OfflineSession } from './store.js';

/**
 * The signed-in person's store on this device, opened once per person.
 * When the browser offers no IndexedDB (private windows of some browsers),
 * the portal still works online: nothing is kept for offline use.
 */
const stores = new Map<string, Promise<LocalStore>>();

export function openStore(userId: string): Promise<LocalStore> {
  let store = stores.get(userId);
  if (!store) {
    store = LocalStore.open(userId);
    stores.set(userId, store);
    store.catch(() => stores.delete(userId));
  }
  return store;
}

/** Keeps the signed-in session on this device, so the app opens offline until it ends. */
export async function rememberSession(
  session: Extract<SessionPayload, { state: 'signed_in' }>,
  rules: AuthRules,
): Promise<void> {
  try {
    const store = await openStore(session.user.id);
    await store.saveSession({ session, rules });
    await setCurrentUserId(session.user.id);
  } catch {
    // No local storage: online use only.
  }
}

/**
 * The session kept on this device: usable until the time the server gave
 * (the offline session, 7 days), 'expired' after it, null when nobody is
 * signed in here. Saved captures stay on the device in every case.
 */
export async function savedSession(now = Date.now()): Promise<OfflineSession | 'expired' | null> {
  try {
    const userId = await currentUserId();
    if (!userId) return null;
    const saved = await (await openStore(userId)).loadSession();
    if (!saved) return null;
    return Date.parse(saved.session.sessionExpiresAt) > now ? saved : 'expired';
  } catch {
    return null;
  }
}

/**
 * Signed out (or the server ended the session): the app no longer opens
 * offline for this person. Their data and unsent changes stay, encrypted,
 * and are sent at their next sign-in on this device.
 */
export async function forgetSession(): Promise<void> {
  try {
    const userId = await currentUserId();
    if (!userId) return;
    await (await openStore(userId)).forgetSession();
    await setCurrentUserId(null);
  } catch {
    // Nothing kept.
  }
}

/** For tests: forget the open stores. */
export function resetStoresForTests(): void {
  for (const store of stores.values())
    void store.then(
      (s) => s.close(),
      () => undefined,
    );
  stores.clear();
}
